import { create } from 'zustand';
import type { ISaaSModule, CRMRecord, SaaSUser } from '@/types/saas';
import type { ITPAModule } from '@/types/tpa';
import type { ModuleContext } from '@/types/auth';
import { saasRegistry, tpaRegistry, SAAS_EXTERNAL_SDKS, loadExternalSDK } from '@/config/module-registry';
import { SAAS_ALIASES } from '@/config/constants';
import { clearProxyCache } from '@/lib/api/proxy';
import { getWebhookAuthToken } from '@/lib/firebase/functions';
import { openAuthPopup } from '@/lib/utils/sso';
import {
  markTPAReauthWipe,
  runPendingTPAReauthWipe,
  broadcastTPAReauth,
  listenForTPAReauthInOtherTabs,
  TPA_OAUTH_POPUP_KEY,
} from '@/modules/tpa/tpa-reauth-cleanup';
import { useAppStore } from './app-store';
import { useAuthStore } from './auth-store';
import { useSettingsStore } from './settings-store';

/**
 * Cloud Functions base URL — must match oauth-callback.tsx.
 */
const CF_BASE = (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true')
  ? 'https://fluidal-geophilous-ellyn.ngrok-free.dev/ulgebra-license/us-central1'
  : 'https://us-central1-ulgebra-license.cloudfunctions.net';

/**
 * TPA side of the same problem. Set (sessionStorage flag) by the Connections
 * page when the user explicitly clicks Update Credentials / Re-Authorize on
 * an ALREADY-connected TPA — meaning the provider account may be about to
 * change. Consumed ONLY at explicit completion points — saveTPACredentials
 * (manual credential save) and the credential dialog's OAuth-popup success —
 * never from generic checkTPAAuth() (which unrelated actions call: dialog
 * close, AI-model save, the Connections refresh button; the old credentials
 * still verify after a cancel, so consuming there wiped caches and reloaded
 * mid-work). Consumption purges the TPA-account-scoped persistents (senders
 * live in `org_data`, WhatsApp templates in `templates`) and hard-reloads so
 * no store keeps the previous account's data. If a re-auth cancels, the flag
 * lingers until the next boot, where purgeTPAReauthCachesAtBoot() clears it
 * with a harmless same-account purge — no reload. First-time credential
 * entry never sets the flag.
 */
export const TPA_REAUTH_PENDING_KEY = 'ua_tpa_reauth_pending';

/**
 * SaaS side of the same flag. Set by the Connections page's Re-Authorize
 * button, which also clears `org_data` eagerly — this is the backstop for
 * entries that get rewritten with the OLD org's data while the auth popup
 * is still open (a lookup refetch, a record-context load). Consumed by the
 * boot purge below, which runs before any store reads the cache.
 */
export const SAAS_REAUTH_PENDING_KEY = 'ua_saas_reauth_pending';

let tpaReauthConsuming = false;

export function consumeTPAReauthAndReload(opts: { accountChanged?: boolean } = {}): boolean {
  // Default true: OAuth completions cannot tell here; the backend checks the
  // account-changed stamp on the extension doc before wiping anything.
  // Returns true when it took over (a reload follows), false when no re-auth
  // was pending so the caller must do its own reload.
  const accountChanged = opts.accountChanged ?? true;
  try {
    if (sessionStorage.getItem(TPA_REAUTH_PENDING_KEY) !== '1') return false;
  } catch {
    return false; // sessionStorage unavailable — nothing set, nothing to do
  }
  if (tpaReauthConsuming) return true; // e.g. OAuth postMessage + window focus both firing
  tpaReauthConsuming = true;
  console.log('[ModuleStore] TPA re-auth completed — purging account caches and reloading');
  // KAN-135: also wipe the old account's per-user history (notifications,
  // activity logs, workflow error logs) on the backend. Flagged BEFORE the
  // async work so a reload that interrupts the call is finished at boot —
  // which is why the reload only waits a moment for it, never the full call.
  const extensionName = useAppStore.getState().extensionName;
  if (accountChanged) {
    markTPAReauthWipe(extensionName);
    broadcastTPAReauth(extensionName); // other tabs of this app reload too
  }
  import('@/lib/cache/idb-cache')
    .then(({ idbCache }) => Promise.allSettled([
      idbCache.clear('org_data'),
      idbCache.clear('templates'),
      idbCache.clear('proxy_cache'), // provider API responses are keyed by uid/app, not account
      accountChanged
        ? Promise.race([runPendingTPAReauthWipe(), new Promise((r) => setTimeout(r, 1500))])
        : Promise.resolve(),
    ]))
    .catch(() => {})
    .then(() => {
      // Remove only AFTER the purge settles: if an external reload (prod's
      // authorization-success popup reloading the opener) interrupts the
      // purge, the surviving flag makes the next boot finish it.
      try {
        sessionStorage.removeItem(TPA_REAUTH_PENDING_KEY);
        sessionStorage.removeItem(TPA_OAUTH_POPUP_KEY);
      } catch { /* */ }
      window.location.reload();
    });
  return true;
}

/**
 * Boot-side completion of a pending TPA or SaaS re-auth. Three ways a flag
 * reaches a fresh boot: (a) prod's authorization-success popup reloads the
 * opener directly, so no in-page code ran the purge; (b) a cancelled re-auth
 * left the flag behind; (c) the SaaS flag, whose eager clear can be undone by
 * a refetch while the auth popup is open. Either way the right move at boot is
 * the same — purge the account caches BEFORE any store reads them, clear the
 * flags, and do NOT reload (this load is already fresh). A cancelled re-auth
 * just costs one same-account refetch. Must be awaited before stores hydrate.
 */
export async function purgeReauthCachesAtBoot(): Promise<void> {
  try {
    if (sessionStorage.getItem(TPA_REAUTH_PENDING_KEY) !== '1' &&
        sessionStorage.getItem(SAAS_REAUTH_PENDING_KEY) !== '1') return;
  } catch {
    return;
  }
  console.log('[ModuleStore] Pending re-auth found at boot — purging account caches');
  // Bounded, because boot AWAITS this: openDB() has no onblocked handler and
  // clear() has no tx.onabort, so a stalled IDB (or a chunk fetch that never
  // resolves) would leave this pending forever — init would never reach
  // setReady() and the app would sit on the loading screen. Same 1500ms guard
  // as the sign-out purge in auth-store.
  let purged = false;
  await Promise.race([
    (async () => {
      const { idbCache } = await import('@/lib/cache/idb-cache');
      await Promise.allSettled([
        idbCache.clear('org_data'),
        idbCache.clear('templates'),
        idbCache.clear('proxy_cache'),
      ]);
      purged = true;
    })().catch(() => { /* best-effort */ }),
    new Promise((resolve) => setTimeout(resolve, 1500)),
  ]);
  // Only drop the flag once the purge actually finished. If it timed out the
  // caches still hold the old account's data, so the next boot must retry.
  if (!purged) {
    console.warn('[ModuleStore] Boot purge timed out — leaving the re-auth flag for the next boot');
    return;
  }
  try {
    sessionStorage.removeItem(TPA_REAUTH_PENDING_KEY);
    sessionStorage.removeItem(SAAS_REAUTH_PENDING_KEY);
    sessionStorage.removeItem(TPA_OAUTH_POPUP_KEY);
  } catch { /* */ }
}

interface ModuleStore {
  saasModule: ISaaSModule | null;
  tpaModule: ITPAModule | null;
  saasLoading: boolean;
  tpaLoading: boolean;
  saasError: string | null;
  tpaError: string | null;

  // TPA auth state
  tpaAuthenticated: boolean;
  tpaAuthChecking: boolean;
  tpaAuthError: string | null;
  /** Specific error code from checkAuth(), e.g. 'WABA_NOT_CONFIGURED' for Embedded Signup */
  tpaAuthErrorCode: string | null;

  // SaaS auth state
  saasAuthenticated: boolean;
  saasAuthChecking: boolean;
  saasAuthChecked: boolean;
  saasAuthError: string | null;
  /** Non-auth SaaS access error (e.g. admin hasn't enabled API access for the
   *  current user's profile). Re-authorization won't fix this, so the prompt
   *  should show a different CTA (e.g. link to the provider's help article).
   *  Currently only set by Zoho CRM for `NO_PERMISSION` (HTTP 403). */
  saasAccessError: string | null;
  setSaasAccessError(code: string | null): void;
  /** Cached SaaS user from checkSaaSAuth (avoids duplicate getCurrentUser calls) */
  saasUser: SaaSUser | null;

  // TPA version tracking (for detecting version changes after deferred checkAuth)
  tpaVersion: string | null;

  // Webhook token for creating services
  webhookToken: { tpa: string; saas: string } | null;

  // Cached CRM records from widget context (app init)
  crmRecords: CRMRecord[];
  /** True while CRM records are being fetched during app init */
  crmRecordsLoading: boolean;
  /**
   * Fine-grained load phase — describes WHICH part of the CRM-record
   * pipeline is currently in flight, so UIs (chat-view placeholder,
   * compose loader) can show an honest "what we're doing right now"
   * label instead of a generic spinner.
   *
   *   'idle'                 — no record context, nothing in flight
   *   'cache-lookup'         — `idbCache.get` is awaiting (very brief)
   *   'cache-hit-bg-refresh' — IDB hit applied, bg `fetchRecords +
   *                            getModuleFields` running to update
   *                            stale phones / module fields
   *   'fetching-fresh'       — IDB miss (or empty cache), pulling
   *                            records straight from the SaaS API
   *   'done'                 — load finished; cleared on next mount
   *
   * Independent of `crmRecordsLoading`: the boolean still gates the
   * skeleton / blocking UI (false-on-cache-hit so the UI unblocks
   * instantly), the phase reports the more detailed step-name.
   */
  crmRecordsLoadPhase: 'idle' | 'cache-lookup' | 'cache-hit-bg-refresh' | 'fetching-fresh' | 'done';
  /** Total number of records expected (for progress bar) */
  crmRecordsTotal: number;
  /** Number of records fetched so far (for progress bar) */
  crmRecordsLoaded: number;
  setCrmRecords(records: CRMRecord[]): void;
  appendCrmRecord(record: CRMRecord): void;
  setCrmRecordsLoading(loading: boolean): void;
  setCrmRecordsLoadPhase(phase: 'idle' | 'cache-lookup' | 'cache-hit-bg-refresh' | 'fetching-fresh' | 'done'): void;
  setCrmRecordsProgress(total: number, loaded: number): void;
  clearCrmRecords(): void;

  loadModules(saasCode: string, tpaCode: string, context: ModuleContext): Promise<void>;
  fetchWebhookToken(appCode: string): Promise<void>;
  checkTPAAuth(): Promise<boolean>;
  setTPAAuthenticated(authenticated: boolean): void;
  saveTPACredentials(credentials: Record<string, string>): Promise<void>;
  checkSaaSAuth(): Promise<boolean>;
  setSaaSAuthenticated(authenticated: boolean): void;
  initiateSaaSAuth(region?: string, domain?: string): string | null;
  initiateSaaSNonAdminAuth(): void;
  getSaaSAuthUrl(region?: string, domain?: string): string | null;
  initiateTPAAuth(): string | null;
  getTPAAuthUrl(): string | null;
  unloadModules(): void;
}

/**
 * Legacy webhook tokens decode to `appCode.uid.tokenId` (3 dot-parts); short
 * v2 acuity tokens decode to `uid.rand` (2). v2 is base64url without padding,
 * which makes atob throw — that also means "not legacy".
 */
function isLegacyWebhookToken(token: string): boolean {
  try {
    return atob(token).split('.').length >= 3;
  } catch {
    return false;
  }
}

export const useModuleStore = create<ModuleStore>((set, get) => ({
  saasModule: null,
  tpaModule: null,
  saasLoading: false,
  tpaLoading: false,
  saasError: null,
  tpaError: null,

  // TPA auth state
  tpaAuthenticated: false,
  tpaAuthChecking: false,
  tpaAuthError: null,
  tpaAuthErrorCode: null,

  // TPA version tracking
  tpaVersion: null,

  // SaaS auth state
  saasAuthenticated: false,
  saasAuthChecking: false,
  saasAuthChecked: false,
  saasAuthError: null,
  saasAccessError: null,
  setSaasAccessError: (code) => set({ saasAccessError: code }),
  saasUser: null,

  // Webhook token
  webhookToken: null,

  // Cached CRM records
  crmRecords: [],
  crmRecordsLoading: false,
  crmRecordsLoadPhase: 'idle',
  crmRecordsTotal: 0,
  crmRecordsLoaded: 0,
  setCrmRecords: (records) => set({ crmRecords: records }),
  appendCrmRecord: (record) => set((s) => ({ crmRecords: [...s.crmRecords, record], crmRecordsLoaded: s.crmRecordsLoaded + 1 })),
  setCrmRecordsLoading: (loading) => set({ crmRecordsLoading: loading }),
  setCrmRecordsLoadPhase: (phase) => set({ crmRecordsLoadPhase: phase }),
  setCrmRecordsProgress: (total, loaded) => set({ crmRecordsTotal: total, crmRecordsLoaded: loaded }),
  clearCrmRecords: () => set({ crmRecords: [], crmRecordsLoading: false, crmRecordsLoadPhase: 'idle', crmRecordsTotal: 0, crmRecordsLoaded: 0 }),

  loadModules: async (saasCode, tpaCode, context) => {
    const startTime = performance.now();
    const logTiming = (msg: string) => {
      const elapsed = performance.now() - startTime;
      console.log(`[ModuleStore +${elapsed.toFixed(0)}ms]`, msg);
    };

    logTiming('loadModules called');

    // Resolve aliases (e.g. ensure consistent saas code)
    const resolvedSaas = SAAS_ALIASES[saasCode] || saasCode;
    if (resolvedSaas !== saasCode) {
      logTiming(`SaaS alias resolved: ${saasCode} → ${resolvedSaas}`);
    }

    set({
      saasLoading: true,
      tpaLoading: true,
      saasError: null,
      tpaError: null,
      tpaAuthenticated: false,
      tpaAuthChecking: false,
      tpaAuthError: null,
      tpaAuthErrorCode: null,
      saasAuthenticated: false,
      saasAuthChecking: false,
      saasAuthChecked: false,
      saasAuthError: null,
      saasAccessError: null,
      saasUser: null,
      // Clear stale CRM records from previous context to prevent data leakage
      crmRecords: [],
    });

    // Load external SDKs — must complete BEFORE SaaS module init
    // (e.g. Zoho CRM SDK must be available when initWidgetContext runs)
    // Only load if inside an iframe (embedded in CRM) — SDK is useless outside
    const isEmbedded = (() => { try { return window.self !== window.top; } catch { return true; } })();
    const externalSDKs = SAAS_EXTERNAL_SDKS[resolvedSaas];
    if (externalSDKs && isEmbedded) {
      logTiming('Starting external SDK load');
      console.log('[ModuleStore] Loading external SDKs:', externalSDKs);
      try {
        await Promise.all(externalSDKs.map(loadExternalSDK));
        logTiming('External SDKs loaded');
        console.log('[ModuleStore] SDK loaded. window.ZOHO:', !!window.ZOHO, 'embeddedApp:', !!(window as unknown as Record<string, unknown>).ZOHO && !!(window.ZOHO as Record<string, unknown>)?.embeddedApp);
      } catch (error) {
        logTiming('External SDK load failed (non-critical)');
        console.warn('Failed to load external SDK:', error);
      }
    } else if (externalSDKs) {
      logTiming('Skipping external SDK load (not inside iframe)');
      console.log('[ModuleStore] isEmbedded=%s, skipping SDK load', isEmbedded);
    }

    // Load SaaS and TPA modules in parallel for faster startup
    logTiming('Starting SaaS module load');
    const saasPromise = (async () => {
      const saasFactory = saasRegistry[resolvedSaas];
      if (saasFactory) {
        try {
          const mod = await saasFactory();
          logTiming('SaaS factory loaded');
          const saasModule = mod.createModule();
          await saasModule.initialize(context);
          logTiming('SaaS module initialized');
          set({ saasModule, saasLoading: false });
        } catch (error) {
          set({
            saasError: error instanceof Error ? error.message : `Failed to load SaaS module: ${resolvedSaas}`,
            saasLoading: false,
          });
        }
      } else {
        set({
          saasError: `Unknown SaaS module: ${resolvedSaas}`,
          saasLoading: false,
        });
      }
    })();

    logTiming('Starting TPA module load');
    const tpaPromise = (async () => {
      const tpaFactory = tpaRegistry[tpaCode];
      if (tpaFactory) {
        try {
          const mod = await tpaFactory();
          logTiming('TPA factory loaded');
          const tpaModule = mod.createModule();
          await tpaModule.initialize(context);
          logTiming('TPA module initialized');

          // Optimistic loading: assume authenticated and show app immediately
          // Defer auth verification until after page load to avoid blocking initial render
          const initialVersion = typeof (tpaModule as any).version === 'string' ? (tpaModule as any).version : null;
          console.log('[ModuleStore] TPA module loaded, assuming authenticated (will verify after page load)', initialVersion ? `version=${initialVersion}` : '');
          set({
            tpaModule,
            tpaLoading: false,
            tpaAuthenticated: true,
            tpaAuthChecking: false,
            tpaAuthError: null,
            tpaVersion: initialVersion,
          });

          // Conversations fetch is handled by ConversationsView which waits for
          // senders + settings to resolve first (so the inbox default sender is applied).

          // Verify auth in background AFTER settings are loaded + a delay.
          // Settings restore selectedAdminUserId which is needed for shared
          // admin access — checkAuth must include _ua_lic_adminUserID.
          // Extra 3s delay reduces initial network burst — the app is already
          // rendering with optimistic tpaAuthenticated=true.
          (async () => {
            // Wait for settings to finish loading (polls every 100ms, max 10s)
            const MAX_WAIT = 10_000;
            const start = Date.now();
            while (!useSettingsStore.getState().settingsLoaded && Date.now() - start < MAX_WAIT) {
              await new Promise(r => setTimeout(r, 100));
            }

            // Delay checkAuth so it doesn't compete with senders/conversations/credits fetches
            // Skip delay in E2E mode — no real network, and the delay makes tests wait ~5s
            // for the TPA auth dialog to appear.
            // KAN-135: an owed old-account history wipe (flag left by a reload
            // that cut it short, or by the authorization-success popup) runs
            // now — before the defer, and whatever checkAuth says — so old
            // notifications are gone by the time the user opens the bell.
            // No-op unless the wipe flag is set. Other tabs of this app get
            // reloaded when one of them switches account.
            void runPendingTPAReauthWipe();
            listenForTPAReauthInOtherTabs(() => useAppStore.getState().extensionName);

            const deferMs = import.meta.env.VITE_E2E ? 0 : 3000;
            await new Promise(r => setTimeout(r, deferMs));
            logTiming('Settings loaded + delay elapsed, starting deferred checkAuth');

            try {
              console.log('[ModuleStore] Starting deferred checkAuth...');
              const isAuthenticated = await tpaModule.checkAuth();
              console.log('[ModuleStore] Deferred checkAuth result:', isAuthenticated);

              if (isAuthenticated) {
                // Check if TPA version changed (e.g. onInit failed to detect version
                // from Firestore, but checkAuth corrected it). If version changed,
                // senders were fetched with the wrong API → must clear cache and re-fetch.
                const currentVersion = typeof (tpaModule as any).version === 'string' ? (tpaModule as any).version : null;
                const prevVersion = get().tpaVersion;
                const versionChanged = prevVersion !== null && currentVersion !== null && currentVersion !== prevVersion;

                if (versionChanged) {
                  console.log(`[ModuleStore] TPA version changed: ${prevVersion} → ${currentVersion}, triggering senders re-fetch`);
                  // Clear IDB senders cache so stale data is not reused
                  const ext = useAppStore.getState().extensionName || '';
                  if (ext) {
                    const { idbCache } = await import('@/lib/cache/idb-cache');
                    await idbCache.delete('org_data', `senders_${ext}`).catch(() => {});
                  }
                  // Toggle tpaAuthenticated to trigger useSenders re-fetch
                  set({ tpaAuthenticated: false, tpaVersion: currentVersion });
                  await new Promise(r => setTimeout(r, 50));
                  set({ tpaAuthenticated: true, tpaAuthChecking: false, tpaAuthError: null, tpaAuthErrorCode: null });
                } else {
                  // Auth passed. Already optimistically set to true — no need to
                  // toggle unless it was somehow cleared. Avoid toggling because
                  // false→true triggers useSenders re-fetch → conversation list reload.
                  if (currentVersion) set({ tpaVersion: currentVersion });
                  if (!get().tpaAuthenticated) {
                    set({ tpaAuthenticated: true, tpaAuthChecking: false, tpaAuthError: null, tpaAuthErrorCode: null });
                  }
                  console.log('[ModuleStore] Auth verified (tpaAuthenticated already true, no toggle)');
                }
              } else {
                console.log('[ModuleStore] Background verification failed, credentials required');
                // Use detailed error message from the TPA module if available (e.g. Facebook OAuthException)
                const detailedError = (tpaModule as Record<string, unknown>).lastAuthErrorMessage as string | null;
                set({
                  tpaAuthenticated: false,
                  tpaAuthChecking: false,
                  tpaAuthError: detailedError || 'Credentials required',
                  tpaAuthErrorCode: tpaModule.lastAuthErrorCode ?? null,
                });
              }
            } catch (authError) {
              console.error('[ModuleStore] Deferred checkAuth threw:', authError);
              set({
                tpaAuthenticated: false,
                tpaAuthChecking: false,
                tpaAuthError: 'Failed to verify credentials',
                tpaAuthErrorCode: null,
              });
            }
          })();
        } catch (error) {
          set({
            tpaError: error instanceof Error ? error.message : `Failed to load TPA module: ${tpaCode}`,
            tpaLoading: false,
          });
        }
      } else {
        set({
          tpaError: `Unknown TPA module: ${tpaCode}`,
          tpaLoading: false,
        });
      }
    })();

    // Only wait for TPA module (needed for conversations)
    // SaaS module loads in background (only needed for CRM features)
    await tpaPromise;
    logTiming('TPA module ready, continuing init');

    // SaaS continues loading in background
    saasPromise.then(() => logTiming('SaaS module ready (background)'));
  },

  /**
   * Re-check TPA authentication (e.g. after saving credentials).
   */
  checkTPAAuth: async () => {
    const { tpaModule } = get();
    if (!tpaModule) return false;

    set({ tpaAuthChecking: true, tpaAuthError: null, tpaAuthErrorCode: null });
    try {
      const isAuthenticated = await tpaModule.checkAuth();
      const detailedError = (tpaModule as Record<string, unknown>).lastAuthErrorMessage as string | null;
      set({
        tpaAuthenticated: isAuthenticated,
        tpaAuthChecking: false,
        tpaAuthError: isAuthenticated ? null : (detailedError || 'Credentials required'),
        tpaAuthErrorCode: tpaModule.lastAuthErrorCode ?? null,
      });
      // Deliberately NOT consuming the re-auth flag here: this runs from
      // unrelated call sites (dialog close, AI-model save, refresh buttons)
      // where the OLD credentials still verify — consuming on those wiped
      // caches and reloaded mid-work. Explicit completion points call
      // consumeTPAReauthAndReload() themselves.
      return isAuthenticated;
    } catch {
      set({
        tpaAuthenticated: false,
        tpaAuthChecking: false,
        tpaAuthError: 'Failed to verify credentials',
        tpaAuthErrorCode: null,
      });
      return false;
    }
  },

  setTPAAuthenticated: (authenticated) =>
    set({ tpaAuthenticated: authenticated, tpaAuthError: authenticated ? null : 'Credentials required' }),

  /**
   * Validate and save TPA credentials, then mark as authenticated.
   *
   * Flow:
   * 1. validateCredentials() — direct API call to the provider (e.g., Twilio)
   *    using the user-supplied credentials with Basic Auth, bypassing the proxy cache.
   * 2. saveCredentials() — writes to Firestore (3-step process).
   * 3. Mark as authenticated immediately. We trust the validation result
   *    because it was a direct API call. We do NOT re-call checkAuth() here
   *    because it goes through the Cloud Function proxy which may still have
   *    a cached 401 from before credentials were saved (GET_CACHED_ONE_DAY).
   * 4. Ensure messaging service/webhook exists (for Twilio, creates Messaging Service)
   */
  saveTPACredentials: async (credentials) => {
    const { tpaModule, webhookToken, fetchWebhookToken } = get();
    if (!tpaModule) throw new Error('No TPA module loaded');

    // 1. Validate credentials directly with the provider
    const validation = await tpaModule.validateCredentials(credentials);
    if (!validation.valid) {
      throw new Error(validation.error || 'Invalid credentials');
    }

    // 2. Save to Firestore
    await tpaModule.saveCredentials(credentials);

    // 2b. Clear API response cache so subsequent calls use fresh credentials
    try {
      // Flush cached provider responses AND the encrypted-credential header slot;
      // otherwise the webhook check below still runs as the OLD account and can
      // write the old service_sid straight back.
      clearProxyCache();
    } catch { /* non-critical */ }

    // 3. Mark as authenticated immediately — validation already confirmed the creds work
    set({
      tpaAuthenticated: true,
      tpaAuthChecking: false,
      tpaAuthError: null,
    });

    // 4. Ensure messaging service/webhook exists
    // For Twilio, this creates a Messaging Service with webhook URLs
    try {
      // Fetch webhook token if not already available
      if (!webhookToken) {
        const extensionName = useAppStore.getState().extensionName;
        if (extensionName) {
          await fetchWebhookToken(extensionName);
        }
      }

      // Check if webhook/service already exists, create if not
      const webhookExists = await tpaModule.checkWebhookExists();
      if (!webhookExists) {
        console.log('[ModuleStore] Creating messaging service/webhook...');
        const { webhookToken: token } = get();
        if (token?.tpa) {
          await tpaModule.createWebhook({ token: token.tpa });
        } else {
          console.warn('[ModuleStore] No webhook token available, skipping service creation');
        }
      } else {
        console.log('[ModuleStore] Messaging service/webhook already exists');
      }
    } catch (error) {
      console.warn('[ModuleStore] Failed to create messaging service:', error);
      // Don't throw - credentials are saved, service creation is optional —
      // but say so, since sends can silently lose their Messaging Service.
      import('sonner')
        .then(({ toast }) => toast.warning('Connected, but the messaging service could not be set up. Open Settings → Incoming Sync to retry.'))
        .catch(() => {});
    }

    // 5. Twilio: set up the Event Streams Sink now rather than waiting for
    //    the next app-init pass (use-app-init step 6b). Non-blocking —
    //    credentials are already saved, this is best-effort.
    const moduleWithSink = tpaModule as unknown as { ensureEventSink?: () => Promise<void> };
    if (typeof moduleWithSink.ensureEventSink === 'function') {
      moduleWithSink.ensureEventSink().catch((err: unknown) => {
        console.warn('[ModuleStore] ensureEventSink after credential save failed:', err);
      });
    }

    // 6. Completes an explicit Update Credentials from the Connections page
    //    (flag set there; no-op on first-time credential entry). Same-account
    //    re-entry keeps history: no wipe.
    const changed = (tpaModule as { lastCredentialSaveChangedAccount?: boolean }).lastCredentialSaveChangedAccount ?? true;
    consumeTPAReauthAndReload({ accountChanged: changed });
  },

  /**
   * Check SaaS authentication by calling getCurrentUser.
   * If it fails with 401, the user needs to authorize.
   * Clears the proxy cache before checking to ensure fresh result.
   */
  checkSaaSAuth: async () => {
    const { saasModule } = get();
    if (!saasModule) return false;

    // On re-check (after auth flow), clear cache so fresh credentials are used
    // Skip on initial check (saasAuthChecked=false) to preserve cache
    if (get().saasAuthChecked) {
      try {
        clearProxyCache();
      } catch { /* non-critical */ }
      console.log('[ModuleStore] Checking SaaS auth (cache cleared for re-check)...');
    } else {
      console.log('[ModuleStore] Checking SaaS auth (initial, using cache)...');
    }

    set({ saasAuthChecking: true, saasAuthError: null });
    try {
      const user = await saasModule.getCurrentUser();
      console.log('[ModuleStore] SaaS getCurrentUser success:', user);
      set({
        saasAuthenticated: true,
        saasAuthChecking: false,
        saasAuthChecked: true,
        saasAuthError: null,
        saasAccessError: null,
        saasUser: user,
      });
      return true;
    } catch (error) {
      console.log('[ModuleStore] SaaS getCurrentUser failed:', error);
      const message = error instanceof Error ? error.message : 'Authorization required';
      set({
        saasAuthenticated: false,
        saasAuthChecking: false,
        saasAuthChecked: true,
        saasAuthError: message,
      });
      return false;
    }
  },

  setSaaSAuthenticated: (authenticated) =>
    set({
      saasAuthenticated: authenticated,
      saasAuthError: authenticated ? null : 'Authorization required',
    }),

  /**
   * Get the SaaS OAuth authorization URL without opening it.
   * @param region Optional datacenter region code (e.g. "US", "EU", "IN") to set before building URL.
   */
  getSaaSAuthUrl: (region?: string, domain?: string) => {
    const { saasModule } = get();
    if (!saasModule) return null;

    const authProcessor = saasModule.getAuthProcessor();
    if (!authProcessor) return null;

    if (region) {
      authProcessor.setDomainFromRegion(region);
    }
    if (domain) {
      authProcessor.currentDomain = domain;
    }
    return authProcessor.getAuthUrl?.(saasModule.oauthScopes) ?? null;
  },

  /**
   * Initiate SaaS OAuth flow (opens popup).
   * @param region Optional datacenter region code (e.g. "US", "EU", "IN") to set before auth.
   * @param domain Optional domain (e.g. subdomain) for OAuth URLs that need it (e.g. Zendesk Support).
   * @returns The auth URL that was opened, or null if it couldn't be built.
   */
  initiateSaaSAuth: (region?: string, domain?: string) => {
    const { saasModule } = get();
    if (!saasModule) return null;

    const authProcessor = saasModule.getAuthProcessor();
    if (!authProcessor) return null;

    if (region) {
      authProcessor.setDomainFromRegion(region);
    }
    if (domain) {
      authProcessor.currentDomain = domain;
    }
    authProcessor.initiateAuth(saasModule.oauthScopes);
    return authProcessor.getAuthUrl?.(saasModule.oauthScopes) ?? null;
  },

  /**
   * Initiate non-admin SaaS OAuth flow (reduced scopes).
   * Only supported by modules that implement initiateNonAdminAuth (e.g. HubSpot).
   */
  initiateSaaSNonAdminAuth: () => {
    const { saasModule } = get();
    if (!saasModule?.initiateNonAdminAuth) return;
    saasModule.initiateNonAdminAuth();
  },

  /**
   * Fetch webhook auth token from Cloud Function.
   * This token is used in webhook URLs to authenticate incoming requests.
   */
  fetchWebhookToken: async (appCode: string) => {
    // Cache key is scoped to appCode AND the logged-in uid (AND impersonated
    // uid if any). Without the uid, switching accounts in the same browser
    // served another user's cached token — webhook URLs built from it then
    // failed auth on the server because the token's owner doc didn't match.
    const { useAuthStore } = await import('@/stores/auth-store');
    const { impersonatedUserId, user } = useAuthStore.getState();
    const uidScope = user?.uid ? `_${user.uid}` : '';
    // Acuity apps moved to short v2 tpa tokens (Acuity caps webhook targets at
    // 256 encoded chars — KAN-85). The _V2 key abandons any cached long token
    // once so the server-issued short one is fetched; other apps keep their key.
    const tokenVersion = appCode.startsWith('acuityscheduling') ? '_V2' : '';
    const cacheKey = impersonatedUserId
      ? `PERSONAL_WEBHOOK_TOKEN${tokenVersion}_${appCode}_impersonate_${impersonatedUserId}`
      : `PERSONAL_WEBHOOK_TOKEN${tokenVersion}_${appCode}${uidScope}`;
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const token = JSON.parse(cached);
        // Acuity apps must use the short v2 tpa token (KAN-85). A legacy long
        // token can land under the V2 cache key when the fetch raced the
        // backend rollout — detect it by shape and refetch instead of serving
        // it forever.
        const staleAcuityToken =
          appCode.startsWith('acuityscheduling') &&
          typeof token?.tpa === 'string' &&
          isLegacyWebhookToken(token.tpa);
        if (token?.tpa && !staleAcuityToken) {
          set({ webhookToken: token });
          return;
        }
      }
    } catch {
      // Ignore parse errors
    }

    // Fetch from Cloud Function (passes _ua_lic_impersonateUserID when
    // present so the CF resolves the impersonated user's token).
    const token = await getWebhookAuthToken(appCode);
    if (token) {
      set({ webhookToken: token });
      // Cache in localStorage
      try {
        localStorage.setItem(cacheKey, JSON.stringify(token));
      } catch {
        // localStorage may be full
      }
    }
  },

  /**
   * Build the TPA OAuth authorization URL using the module's getOAuthInfo().
   * Returns null if the TPA doesn't use OAuth.
   */
  getTPAAuthUrl: () => {
    const { tpaModule } = get();
    if (!tpaModule?.getOAuthInfo) return null;

    const oauthInfo = tpaModule.getOAuthInfo();
    if (!oauthInfo) return null;

    const extensionName = useAppStore.getState().extensionName;
    const uid = useAuthStore.getState().user?.uid;
    if (!extensionName || !uid) return null;

    // For WhatsApp, always use 'whatsappforpage' as the appCode in the state
    // so the oauth-home callback routes to new_oauthCallbackFacebookLogin correctly.
    const stateAppCode = extensionName.startsWith('whatsapp') ? 'whatsappforpage' : extensionName;
    const state = `${uid}:::${stateAppCode}`;

    // If oauthBaseUrl already contains a complete URL with query params
    // (e.g. Facebook/WhatsApp builds the full dialog/oauth URL with client_id,
    // scope, redirect_uri inline), just append the state parameter.
    if (oauthInfo.oauthBaseUrl.includes('?')) {
      return `${oauthInfo.oauthBaseUrl}&state=${state}`;
    }

    // Standard OAuth providers — build URL from components
    const clientId = oauthInfo.oauthClientIds[extensionName] || oauthInfo.oauthClientIds._googleClientId || '';
    if (!clientId) return null;

    const redirectUri = `${CF_BASE}${oauthInfo.oauthCallbackPath}`;

    // Google OAuth uses a different URL format
    if (oauthInfo.oauthBaseUrl.includes('accounts.google.com')) {
      const scopes = 'https://www.googleapis.com/auth/drive.file';
      return `${oauthInfo.oauthBaseUrl}?client_id=${clientId}&redirect_uri=${encodeURIComponent('https://sms.ulgebra.com/oauth-home')}&response_type=code&scope=${encodeURIComponent(scopes)}&state=${state}:::isGoogleAuth&access_type=offline&prompt=consent`;
    }

    const authPath = oauthInfo.oauthAuthorizePath || '/oauth';
    const extraParams = oauthInfo.oauthExtraParams
      ? '&' + new URLSearchParams(oauthInfo.oauthExtraParams).toString()
      : '';
    return `${oauthInfo.oauthBaseUrl}${authPath}?client_id=${clientId}&state=${state}&redirect_uri=${encodeURIComponent(redirectUri)}${extraParams}`;
  },

  /**
   * Initiate TPA OAuth flow by building the URL and opening a popup.
   * Returns the auth URL, or null if the TPA doesn't use OAuth.
   */
  initiateTPAAuth: () => {
    const url = get().getTPAAuthUrl();
    if (!url) return null;

    void openAuthPopup(url);
    return url;
  },

  unloadModules: () => {
    const { saasModule, tpaModule } = get();
    saasModule?.dispose();
    tpaModule?.dispose();
    set({
      saasModule: null,
      tpaModule: null,
      saasLoading: false,
      tpaLoading: false,
      saasError: null,
      tpaError: null,
      tpaAuthenticated: false,
      tpaAuthChecking: false,
      tpaAuthError: null,
      tpaAuthErrorCode: null,
      saasAuthenticated: false,
      saasAuthChecking: false,
      saasAuthChecked: false,
      saasAuthError: null,
      saasUser: null,
    });
  },
}));

// Expose store for E2E testing — lets Playwright trigger checkTPAAuth() immediately
// instead of waiting for the ~10s deferred auth check (settings poll + 3s delay).
if (import.meta.env.DEV) {
  (window as any).__moduleStore = useModuleStore;
}
